import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  inquiryApi,
  INQUIRY_STATUS_LABEL,
  type InquirySummary,
} from '../api/inquiry'

/**
 * 상태 태그.
 *
 * 세 상태가 같은 모양이다 — SmallBadgeButton 의 inverse 변형과 같은 값
 * (bg-472410-10% / text-brown-80%-main / rounded-8px / 14px 700).
 * 상태마다 색을 달리하면 목록이 알록달록해지고, 급한 것도 아니라 눈에 띌 이유가 없다.
 *
 * 컴포넌트를 그대로 못 쓰는 건 SmallBadgeButton 이 <button> 이라서다 —
 * 줄 전체가 이미 버튼이라 버튼 안에 버튼이 들어가면 클릭이 엉킨다.
 */
const StatusTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex-shrink-0 px-16px py-8px rounded-8px bg-472410-10% font-pretendard font-700 text-14px leading-none text-center text-brown-80%-main">
    {label}
  </span>
)

/** "2026-10-24 14:02" → "10월 24일" */
const toDateLabel = (raw: string): string => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(raw ?? '')
  if (!m) return ''
  return `${Number(m[1])}월 ${Number(m[2])}일`
}

/**
 * 문의 내역.
 */
const SupportList: React.FC = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<InquirySummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const res = await inquiryApi.getInquiries()
        setItems(res.items)
        setHasError(false)
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  // 비었을 때·실패했을 때는 칸을 가득 채워 세로 가운데에 둔다.
  // empty-state-center 는 제 높이의 -5% 만큼 올린다 — 기하학적 정중앙은 처져 보인다.
  const centered = (node: React.ReactNode) => (
    <div className="h-full flex items-center justify-center empty-state-center">
      {node}
    </div>
  )

  const renderBody = () => {
    if (isLoading) {
      return centered(<LoadingSpinner size={28} borderWidth={4} />)
    }
    if (hasError) {
      return centered(
        <span className="text-style-body1 text-textColorText3">
          문의를 불러오지 못했어요
        </span>
      )
    }
    if (items.length === 0) {
      return centered(
        <span className="text-style-body1 text-textColorText3">
          아직 보낸 문의가 없어요
        </span>
      )
    }
    return items.map((item) => (
      <button
        key={item.id}
        type="button"
        onClick={() => navigate(`/support/${item.id}`)}
        className="w-full flex items-center gap-10px px-20px py-18px border-b border-brandPrimary-20% text-left active:bg-brandPrimary-5%"
      >
        <div className="flex-1 min-w-0 flex flex-col gap-6px">
          <span className="font-pretendard font-bold text-15px leading-22px text-brown-80%-main truncate">
            {item.subject}
          </span>
          <span className="font-pretendard font-regular text-13px leading-18px text-textColorText3">
            {item.category_label} · {toDateLabel(item.created_at)}
          </span>
        </div>
        <StatusTag label={INQUIRY_STATUS_LABEL[item.status]} />
      </button>
    ))
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden">
        <AppBar title="문의 내역" isBottomBorder />
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
          {renderBody()}
        </div>
      </div>
    </div>
  )
}

export default SupportList
